import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { makeStyles } from 'tss-react/mui'
import type { Theme } from '@mui/material/styles'
import { Paper, Button, TextField, Box } from '@mui/material'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import type { FieldValues } from 'react-hook-form'
import Stack from 'ui/layout/Stack'
import MarginRightCancelButton from 'ui/shorthands/MarginRightCancelButton'
import { Text1 } from 'ui/shorthands/Texts'

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    margin: 'auto',
    width: '100%',
  },
  inputWrapper: {
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  box: {
    width: '100%',
  },
  label: {
    wordBreak: 'keep-all',
  },
}))

export type EligibilityVerificationUrlModalContentProps = {
  url?: string
  onSubmit: (data: { url: string }) => Promise<void>
  onClose: () => void
}

const EligibilityVerificationUrlModalContent: React.FC<
  EligibilityVerificationUrlModalContentProps
> = ({
  url = '',
  onSubmit,
  onClose,
}: EligibilityVerificationUrlModalContentProps) => {
  const { t } = useTranslation()
  const { classes } = useStyles()

  const defaultValues: { url: string } = {
    url,
  }

  const schema = yup.object().shape({
    url: yup
      .string()
      .nullable()
      .matches(
        /^https:\/\/(?:[a-zA-Z0-9-]+\.)?onshikaku\.org(?:\/.*)?(?:\?.*)?$/,
        {
          message: t(
            'validation.URLに誤りがあります。医療機関別URLの内容をご確認の上、再登録をお願いいたします。'
          ),
          excludeEmptyString: true,
        }
      ),
  })

  const {
    handleSubmit,
    control,
    formState: { errors, isValid, isSubmitting },
    setError,
  } = useForm<FieldValues>({
    defaultValues,
    resolver: yupResolver(schema),
    mode: 'onChange',
  })

  const serveError = () => {
    setError('url', {
      type: 'server',
      message: t('errors.オンライン資格確認用URLの保存に失敗しました'),
    })
  }

  return (
    <Paper className={classes.root}>
      <form
        onSubmit={handleSubmit((data) =>
          onSubmit(data as { url: string })
            .then(onClose)
            .catch((e) => {
              serveError()
            })
        )}
      >
        <Stack borderBottom className={classes.inputWrapper}>
          <Box className={classes.box}>
            <Text1>
              <Trans
                i18nKey={
                  'trans.【オンライン資格確認等システム】の「医療機関別URL」でコピーしたURLを貼り付けて、登録してください。...'
                }
              />
            </Text1>
          </Box>
          <Controller
            render={({ field }) => (
              <TextField
                {...field}
                variant="outlined"
                placeholder={'https://...'}
                error={!!errors.url}
                helperText={errors.url?.message}
                fullWidth
              />
            )}
            control={control}
            name={'url'}
          />

          <Box className={classes.box}>
            <Text1>{t('利用停止する場合はURLを削除ください。')}</Text1>
          </Box>
        </Stack>
        <Stack align={'right'}>
          <MarginRightCancelButton onClick={onClose} />
          <Button
            color="primary"
            variant="contained"
            type="submit"
            disabled={!isValid || isSubmitting}
          >
            {t('保存')}
          </Button>
        </Stack>
      </form>
    </Paper>
  )
}

export default EligibilityVerificationUrlModalContent
